import { useParams, useNavigate } from 'react-router-dom';
import type { Id } from '../../../convex/_generated/dataModel'; // DEEP_IMPORT_JUSTIFIED: pages in src/pages/insurance/ require three levels up to reach convex/_generated/
import { usePolicyFormPage } from '@/hooks/usePolicyFormPage';
import { PolicyFormHeader } from '@/components/insurance/PolicyFormHeader';
import { PolicyInformationCard } from '@/components/insurance/PolicyInformationCard';
import { PolicyStatusCard } from '@/components/insurance/PolicyStatusCard';
import { DynamicFieldSections } from '@/components/insurance/DynamicFieldSections';
import { PolicyDocumentsSection } from '@/components/insurance/PolicyDocumentsSection';
import { PolicyApprovalActions } from '@/components/insurance/PolicyApprovalDialog';
import { PolicyApplicationWizard } from '@/components/insurance/policy-application/PolicyApplicationWizard';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { FileText } from 'lucide-react';
import type { TPolicyStatus, TPolicySource } from '@/@types/insurance';
import type { IPolicySetupStepProps } from '@/@types/policyFormTemplates';

export function PolicyFormPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();

  const isEditing = id !== undefined && id !== 'new';
  const policyId = isEditing ? (id as Id<'insurancePolicies'>) : null; // AS_JUSTIFIED: one-time Id cast from route params

  const h = usePolicyFormPage(policyId, isEditing);

  if (!h.orgLoading && !h.currentOrg) {
    return (
      <div className="min-h-full bg-background-secondary py-8 px-10">
        <div className="flex flex-col items-center justify-center py-24 text-center">
          <FileText className="w-12 h-12 text-muted-foreground mb-4" />
          <h3 className="text-lg font-medium text-foreground mb-1">No organisation found</h3>
          <p className="text-sm text-muted-foreground">
            You need to be part of an organisation to manage insurance policies.
          </p>
        </div>
      </div>
    );
  }

  if (h.isLoading) {
    return (
      <div className="min-h-full bg-background-secondary py-8 px-10">
        <div className="flex items-center justify-center py-16">
          <p className="text-sm text-muted-foreground">Loading...</p>
        </div>
      </div>
    );
  }

  // Build policySetupProps for the integrated wizard step 0
  const policySetupProps: IPolicySetupStepProps = {
    isEditing,
    isSubmitting: h.isSubmitting,
    typeList: h.typeList,
    selectedTypeId: h.selectedTypeId,
    onTypeChange: h.setSelectedTypeId,
    selectedTypeName: h.selectedType?.name,
    providerList: h.providerList,
    providerFieldValue: h.form.getFieldValue('Provider'),
    onProviderChange: val => h.form.setFieldValue('Provider', val),
    policyNumber: h.form.policyNumber,
    onPolicyNumberChange: h.form.setPolicyNumber,
    policyStartDate: h.form.policyStartDate,
    onPolicyStartDateChange: h.form.setPolicyStartDate,
    policyExpireDate: h.form.policyExpireDate,
    onPolicyExpireDateChange: h.form.setPolicyExpireDate,
    policyDuration: h.form.policyDuration,
    onPolicyDurationChange: h.form.setPolicyDuration,
    employeePolicyForm: {
      policySource: h.employeePolicyForm.policySource,
      companies: h.employeePolicyForm.companies,
      employees: h.employeePolicyForm.employees,
      selectedCompanyId: h.employeePolicyForm.selectedCompanyId,
      selectedEmployeeId: h.employeePolicyForm.selectedEmployeeId,
      isPrefilledFromUrl: h.employeePolicyForm.isPrefilledFromUrl,
    },
    onEmployeeFormChange: {
      onSourceChange: val => h.employeePolicyForm.handlePolicySourceChange(val as TPolicySource), // AS_JUSTIFIED: value from Select with known source options
      onCompanyChange: companyId => {
        h.employeePolicyForm.setSelectedCompanyId(companyId);
        h.employeePolicyForm.setSelectedEmployeeId('');
      },
      onEmployeeChange: id => h.employeePolicyForm.handleEmployeeChange(id, h.form.customerName),
    },
    status: h.form.status,
    onStatusChange: h.form.setStatus,
    invoiceStatus: h.form.invoiceStatus,
    onInvoiceStatusChange: h.form.setInvoiceStatus,
    premium: h.form.premium,
    onPremiumChange: h.form.setPremium,
    partiallyPaidAmount: h.form.partiallyPaidAmount,
    onPartiallyPaidAmountChange: h.form.setPartiallyPaidAmount,
  };

  // When a form template is found, render the integrated wizard (Policy Setup is step 0 inside)
  const hasTemplate = Boolean(h.formTemplate && h.currentOrg);

  return (
    <div className="min-h-full bg-background">
      <PolicyFormHeader
        isEditing={isEditing}
        isSubmitting={h.isSubmitting}
        policyNumber={h.existingPolicy?.policyNumber}
        isImported={h.existingPolicy?.isImported}
        hasCertificateData={Boolean(h.certificateData)}
        showCertificateDownload={
          h.selectedType?.name?.toLowerCase().includes('health') &&
          h.providerList.some(
            p =>
              p.name.toLowerCase().includes('4icover') && p._id === h.form.getFieldValue('Provider')
          )
        }
        onBack={() => navigate(-1)}
        onCancel={() => navigate(-1)}
        onSave={h.handleSubmit}
        onDownloadCertificate={h.handleDownloadCertificate}
      />

      {h.submitError && <p className="text-sm text-destructive px-10 mb-4">{h.submitError}</p>}

      {hasTemplate ? (
        <PolicyApplicationWizard
          template={h.formTemplate!}
          orgId={h.currentOrg!._id}
          existingPolicyId={h.existingPolicy?._id}
          existingData={h.applicationData?.applicationData}
          policySource={h.policySource}
          policyStatus={h.existingPolicy?.status as TPolicyStatus | undefined} // AS_JUSTIFIED: status field from Convex query matches TPolicyStatus union
          rejectionReason={h.existingPolicy?.rejectionReason}
          companyEmployeeId={h.wizardCompanyEmployeeId}
          onBack={() => navigate(-1)}
          onComplete={() => navigate('/insurance/policies')}
          policySetupProps={policySetupProps}
        />
      ) : (
        <div className="px-10 py-8 flex flex-col gap-7">
          <PolicyInformationCard
            isEditing={isEditing}
            isSubmitting={h.isSubmitting}
            typeList={h.typeList}
            selectedTypeId={h.selectedTypeId}
            onTypeChange={h.setSelectedTypeId}
            selectedTypeName={h.selectedType?.name}
            providerList={h.providerList}
            providerFieldValue={h.form.getFieldValue('Provider')}
            onProviderChange={val => h.form.setFieldValue('Provider', val)}
            policyNumber={h.form.policyNumber}
            onPolicyNumberChange={h.form.setPolicyNumber}
            policyStartDate={h.form.policyStartDate}
            onPolicyStartDateChange={h.form.setPolicyStartDate}
            policyExpireDate={h.form.policyExpireDate}
            onPolicyExpireDateChange={h.form.setPolicyExpireDate}
            policyDuration={h.form.policyDuration}
            onPolicyDurationChange={h.form.setPolicyDuration}
            employeePolicyForm={{
              policySource: h.employeePolicyForm.policySource,
              companies: h.employeePolicyForm.companies,
              employees: h.employeePolicyForm.employees,
              selectedCompanyId: h.employeePolicyForm.selectedCompanyId,
              selectedEmployeeId: h.employeePolicyForm.selectedEmployeeId,
              isPrefilledFromUrl: h.employeePolicyForm.isPrefilledFromUrl,
            }}
            onEmployeeFormChange={{
              onSourceChange: val =>
                h.employeePolicyForm.handlePolicySourceChange(val as TPolicySource), // AS_JUSTIFIED: value from Select with known source options
              onCompanyChange: companyId => {
                h.employeePolicyForm.setSelectedCompanyId(companyId);
                h.employeePolicyForm.setSelectedEmployeeId('');
              },
              onEmployeeChange: id =>
                h.employeePolicyForm.handleEmployeeChange(id, h.form.customerName),
            }}
            benefitPlans={h.benefitPlans}
            selectedBenefitPlanId={h.selectedBenefitPlanId}
            onBenefitPlanChange={h.setSelectedBenefitPlanId}
          />

          <PolicyStatusCard
            isSubmitting={h.isSubmitting}
            status={h.form.status}
            onStatusChange={h.form.setStatus}
            invoiceStatus={h.form.invoiceStatus}
            onInvoiceStatusChange={h.form.setInvoiceStatus}
            premium={h.form.premium}
            onPremiumChange={h.form.setPremium}
            partiallyPaidAmount={h.form.partiallyPaidAmount}
            onPartiallyPaidAmountChange={h.form.setPartiallyPaidAmount}
          />

          {h.existingPolicy && h.currentOrg && (
            <PolicyDocumentsSection
              policyDocuments={h.existingPolicy.policyDocuments ?? []}
              sourceFileIds={h.existingPolicy.sourceFileIds ?? []}
              policyId={h.existingPolicy._id}
              orgId={h.currentOrg._id}
            />
          )}

          {h.existingPolicy?.status === 'under_review' && h.isInOrg && h.currentOrg && (
            <div className="rounded-xl border border-border bg-card p-6">
              <div className="flex items-center gap-2.5 mb-4">
                <h2 className="text-base font-semibold text-foreground">Review Application</h2>
              </div>
              <PolicyApprovalActions
                policyId={h.existingPolicy._id}
                orgId={h.currentOrg._id}
                applicationValues={h.applicationData?.applicationData?.values ?? {}}
                onApproved={() => navigate('/insurance/policies')}
                onRejected={() => navigate('/insurance/policies')}
              />
            </div>
          )}

          <DynamicFieldSections
            groupedSections={h.groupedSections}
            dynamicFields={h.dynamicFields}
            ungroupedFields={h.ungroupedFields}
            supportsDrivers={h.selectedType?.supportsDrivers === true}
            selectedTypeName={h.selectedType?.name}
            driverForm={h.driverForm}
            form={h.form}
            orgId={h.currentOrg?._id}
            isSubmitting={h.isSubmitting}
          />
        </div>
      )}

      {/* Employee switch confirmation dialog */}
      <AlertDialog open={h.employeePolicyForm.showSwitchConfirm}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Switch employee?</AlertDialogTitle>
            <AlertDialogDescription>
              Switching employee will replace the current form values with the new employee&apos;s
              data. Continue?
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={h.employeePolicyForm.cancelEmployeeSwitch}>
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction onClick={h.employeePolicyForm.confirmEmployeeSwitch}>
              Continue
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
